// src/components/resources/tools/generators/SubtitleOverlay/position-styles.ts
//
// Pure position-style resolvers for the subtitle overlay. The former single-file
// component carried two near-duplicate switch statements that mapped a `position`
// string + `margin` into placement styles — one producing a CSS-in-JS *object*
// (the live React preview) and one producing a CSS *string* (the exported HTML
// document). The two output formats genuinely differ (object vs. string), so they
// are unified here into ONE shared module with two verbatim resolvers rather than
// collapsed into a single function — behavior preservation outranks dedup.

/**
 * Resolve the CSS-in-JS position style object from a position string + margin.
 * Verbatim move of the `<SubtitleOverlayPreview>` switch.
 */
export function getPositionStyle(position: string, margin: number): Record<string, string> {
  // Determine position style as an object
  let positionStyle: Record<string, string> = { bottom: '40px', left: '50%', transform: 'translateX(-50%)' }
  switch (position) {
    case 'bottom-center':
      positionStyle = { bottom: `${margin}px`, left: '50%', transform: 'translateX(-50%)' }
      break
    case 'bottom-left':
      positionStyle = { bottom: `${margin}px`, left: `${margin}px` }
      break
    case 'bottom-right':
      positionStyle = { bottom: `${margin}px`, right: `${margin}px` }
      break
    case 'top-center':
      positionStyle = { top: `${margin}px`, left: '50%', transform: 'translateX(-50%)' }
      break
    case 'top-left':
      positionStyle = { top: `${margin}px`, left: `${margin}px` }
      break
    case 'top-right':
      positionStyle = { top: `${margin}px`, right: `${margin}px` }
      break
    case 'center':
      positionStyle = { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }
      break
    case 'center-left':
      positionStyle = { top: '50%', left: `${margin}px`, transform: 'translateY(-50%)' }
      break
    case 'center-right':
      positionStyle = { top: '50%', right: `${margin}px`, transform: 'translateY(-50%)' }
      break
  }
  return positionStyle
}

/**
 * Resolve the CSS-string position declaration from a position string + margin.
 * Verbatim move of the `generateHTML` switch.
 */
export function getPositionCss(position: string, margin: number): string {
  // Determine position style
  let positionCSS = 'bottom: 40px; left: 50%; transform: translateX(-50%);'
  switch (position) {
    case 'bottom-center':
      positionCSS = `bottom: ${margin}px; left: 50%; transform: translateX(-50%);`
      break
    case 'bottom-left':
      positionCSS = `bottom: ${margin}px; left: ${margin}px;`
      break
    case 'bottom-right':
      positionCSS = `bottom: ${margin}px; right: ${margin}px;`
      break
    case 'top-center':
      positionCSS = `top: ${margin}px; left: 50%; transform: translateX(-50%);`
      break
    case 'top-left':
      positionCSS = `top: ${margin}px; left: ${margin}px;`
      break
    case 'top-right':
      positionCSS = `top: ${margin}px; right: ${margin}px;`
      break
    case 'center':
      positionCSS = 'top: 50%; left: 50%; transform: translate(-50%, -50%);'
      break
    case 'center-left':
      positionCSS = `top: 50%; left: ${margin}px; transform: translateY(-50%);`
      break
    case 'center-right':
      positionCSS = `top: 50%; right: ${margin}px; transform: translateY(-50%);`
      break
  }
  return positionCSS
}
